import moment from 'moment';
import isNumber from 'lodash/isNumber';

import { convertMomentToDatePickerTime } from 'helpers/timeFormat';

// Actions
const ADD_SEARCH_TERM = 'zen/DiscoverHistorics/HistoricsSearchForm/ADD_SEARCH_TERM';
const EDIT_SEARCH_TERM = 'zen/DiscoverHistorics/HistoricsSearchForm/EDIT_SEARCH_TERM';
const EDIT_DATE_RANGE = 'zen/DiscoverHistorics/HistoricsSearchForm/EDIT_DATE_RANGE';
const REMOVE_SEARCH_TERM = 'zen/DiscoverHistorics/HistoricsSearchForm/REMOVE_SEARCH_TERM';
const TOGGLE_RECENT_SEARCHES = 'zen/DiscoverHistorics/HistoricsSearchForm/TOGGLE_RECENT_SEARCHES';
const POPULATE_SEARCH_FORM = 'zen/DiscoverHistorics/HistoricsSearchForm/POPULATE_SEARCH_FORM';
const CLEAR_SEARCH_TERMS = 'zen/DiscoverHistorics/HistoricsSearchForm/CLEAR_SEARCH_TERMS';
const SET_FFB_PARSER_ERROR = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_FFB_PARSER_ERROR';
const SET_QUERY_PARAMS = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_QUERY_PARAMS';
const SET_ZQL_ARRAY = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_ZQL_ARRAY';
const SET_ADDITIONAL_FILTERS = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_ADDITIONAL_FILTERS';
const CLOSE_ADDITIONAL_FILTERS = 'zen/DiscoverHistorics/HistoricsSearchForm/CLOSE_ADDITIONAL_FILTERS';
const SET_TRANSLATIONS = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_TRANSLATIONS';
const CLEAR_TRANSLATIONS = 'zen/DiscoverHistorics/HistoricsSearchForm/CLEAR_TRANSLATIONS';
const SET_SELECTED_LANGUAGES = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_SELECTED_LANGUAGES';
const REMOVE_LANGUAGE = 'zen/DiscoverHistorics/HistoricsSearchForm/REMOVE_LANGUAGE';
const SET_KEYWORD_COUNT = 'zen/DiscoverHistorics/HistoricsSearchForm/SET_KEYWORD_COUNT';
const TOGGLE_KEYWORD = 'zen/DiscoverHistorics/HistoricsSearchForm/TOGGLE_KEYWORD';
const MAKE_TRANSLATIONS_OBSOLETE = 'zen/DiscoverHistorics/HistoricsSearchForm/MAKE_TRANSLATIONS_OBSOLETE';

// Using a function to assist in testing (need clock management at runtime)
const _getInitialState = () => {
  return {
    searchTerms: [''],
    selectedLanguages: [[], [], [], [], []],
    translations: [[], [], [], [], []],
    areTranslationsObsolete: [false, false, false, false, false],
    enabledKeywordsCount: [0, 0, 0, 0, 0],
    parsingErrors: [],
    startDate: convertMomentToDatePickerTime(moment().subtract(29, 'day').startOf('day')),
    endDate:  'now',
    recentSearchesOpen: false,
    additionalFilters: [{}, {}, {}, {}, {}]
  };
};

// Reducer
// eslint-disable-next-line complexity
const reducer = (state = _getInitialState(), action = {}) => {
  const { searchTerms } = state;
  const { type, payload } = action;

  switch (type) {
    case ADD_SEARCH_TERM: {
      if(searchTerms.length < 5) {
        return {...state, searchTerms: searchTerms.concat([''])};
      }
      return state;
    }
    case REMOVE_SEARCH_TERM: {
      if(searchTerms.length > 1) {
        return {...state, searchTerms: searchTerms.filter((st, i) => i !== payload.index)};
      }
      return state;
    }
    case EDIT_DATE_RANGE: {
      const { startDate, endDate } = payload;
      return {...state, startDate, endDate};
    }
    case EDIT_SEARCH_TERM: {
      const { value, index } = payload;

      const newSearchTerms = [
        ...searchTerms.slice(0, index),
        value,
        ...searchTerms.slice(index + 1)
      ];
      return {...state, searchTerms: newSearchTerms };
    }
    case TOGGLE_RECENT_SEARCHES: {
      return {...state, recentSearchesOpen: !state.recentSearchesOpen};
    }

    case CLEAR_SEARCH_TERMS: {
      return _getInitialState();
    }
    case SET_FFB_PARSER_ERROR: {
      const zqlArray = payload;
      const parsingErrors = zqlArray.map((item) => {
        if (item.err) {
          return item.err.pop().text;
        }
        return null;
      });
      return {...state, parsingErrors};
    }
    case POPULATE_SEARCH_FORM:
    case SET_QUERY_PARAMS:
    case SET_ZQL_ARRAY:
      return {...state, ...payload };
    case SET_ADDITIONAL_FILTERS: {
      return {...state, additionalFilters: payload};
    }
    case CLOSE_ADDITIONAL_FILTERS: {
      const newAdditionalFilters = state.additionalFilters.map((settings) => ({...settings, open: false}));
      return {...state, additionalFilters: newAdditionalFilters};
    }
    case SET_TRANSLATIONS: {
      const { index, translations } = payload;
      if (isNumber(index)) {
        const areTranslationsObsolete = [...state.areTranslationsObsolete];
        areTranslationsObsolete[index] = false;
        return {...state, translations, areTranslationsObsolete};
      }  // we are updating all
      return {...state, translations, areTranslationsObsolete: [false, false, false, false, false]};

    }
    case MAKE_TRANSLATIONS_OBSOLETE: {
      const { index } = payload;
      const areTranslationsObsolete = [...state.areTranslationsObsolete];
      areTranslationsObsolete[index] = true;
      return {...state, areTranslationsObsolete};
    }
    case SET_KEYWORD_COUNT: {
      return {...state, enabledKeywordsCount: payload};
    }
    case SET_SELECTED_LANGUAGES: {
      const { index, languages } = payload;
      const newSelectedLanguages = [...state.selectedLanguages];
      newSelectedLanguages[index] = languages;
      return {...state, selectedLanguages: newSelectedLanguages};
    }
    case CLEAR_TRANSLATIONS: {
      const { index } = payload;
      const updatedSelectedLanguages = [...state.selectedLanguages];
      const updatedTranslations = [...state.translations];
      const updatedEnabledKeywordsCount = [...state.enabledKeywordsCount];
      const areTranslationsObsolete = [...state.areTranslationsObsolete];
      updatedTranslations[index] = [];
      updatedSelectedLanguages[index] = [];
      updatedEnabledKeywordsCount[index] = 0;
      areTranslationsObsolete[index] = false;
      return {
        ...state,
        translations: updatedTranslations,
        selectedLanguages: updatedSelectedLanguages,
        enabledKeywordsCount: updatedEnabledKeywordsCount,
        areTranslationsObsolete
      };
    }
    case REMOVE_LANGUAGE: {
      const { index, code } = payload;
      const newSelectedLanguages = [...state.selectedLanguages];
      const newTranslations = [...state.translations];
      const newEnabledKeywordsCount = [...state.enabledKeywordsCount];

      newSelectedLanguages[index] = newSelectedLanguages[index].filter(l => l.code !== code);
      newTranslations[index] = newTranslations[index].filter(t => t.language.code !== code);
      newEnabledKeywordsCount[index] = newTranslations[index].reduce(
        (acc, t) => acc + t.keywords.filter(k => k.enabled).length,
        0
      );

      return {
        ...state,
        selectedLanguages: newSelectedLanguages,
        translations: newTranslations,
        enabledKeywordsCount: newEnabledKeywordsCount
      };
    }
    case TOGGLE_KEYWORD: {
      const { index, code, keywordId } = payload;
      const newTranslationState = [...state.translations];
      const newEnabledCountState = [...state.enabledKeywordsCount];

      const targetTranslations = newTranslationState[index];
      const languageIndex = targetTranslations.findIndex(t => t.language.code === code);

      if (languageIndex > -1) {
        const keywordIndex = targetTranslations[languageIndex].keywords.findIndex(k => k.keywordId === keywordId);

        if (keywordIndex > -1) {
          const oldState = targetTranslations[languageIndex].keywords[keywordIndex].enabled;
          targetTranslations[languageIndex].keywords[keywordIndex].enabled = !oldState;

          newTranslationState[index] = targetTranslations;
          newEnabledCountState[index] += oldState ? -1 : 1;
          return { ...state, translations: newTranslationState, enabledKeywordsCount: newEnabledCountState };
        }
      }
      return state;

    }
    default:
      return state;
  }
};

// Action Creators
const addSearchTerm = () => {
  return { type: ADD_SEARCH_TERM };
};

const editSearchTerm = (value, index) => {
  return {
    type: EDIT_SEARCH_TERM,
    payload: { value, index }
  };
};

const editDateRange = (payload) => {
  return { type: EDIT_DATE_RANGE, payload };
};

const removeSearchTerm = (index) => {
  return { type: REMOVE_SEARCH_TERM, payload: { index }};
};

const toggleRecentSearches = () => {
  return { type: TOGGLE_RECENT_SEARCHES };
};

const populateSearchForm = (payload) => {
  return { type: POPULATE_SEARCH_FORM, payload };
};

const clearSearchTerms = () => {
  return { type: CLEAR_SEARCH_TERMS };
};

const setFFBParseErrors = (payload) => {
  return { type: SET_FFB_PARSER_ERROR, payload };
};

const setQueryParams = (payload) => {
  return { type: SET_QUERY_PARAMS, payload };
};
const setZqlArray = (payload) => {
  return { type: SET_ZQL_ARRAY, payload };
};

const setAdditionalFilters = (payload) => {
  return { type: SET_ADDITIONAL_FILTERS, payload };
};

const closeAdditonalFilters = () => {
  return { type: CLOSE_ADDITIONAL_FILTERS };
};

const setTranslations = (payload) => {
  return { type: SET_TRANSLATIONS, payload };
};

const clearTranslations = (payload) => {
  return { type: CLEAR_TRANSLATIONS, payload};
};

const makeTranslationsObsolete = (payload) => {
  return { type: MAKE_TRANSLATIONS_OBSOLETE, payload };
};

const selectLanguages = (payload) => {
  return { type: SET_SELECTED_LANGUAGES, payload };
};

const removeLanguage = (payload) => {
  return { type: REMOVE_LANGUAGE, payload };
};

const setEnabledKeywordsCount = (payload) => {
  return { type: SET_KEYWORD_COUNT, payload };
};

const onToggleKeyword = (payload) => {
  return { type: TOGGLE_KEYWORD, payload };
};

export default reducer;
export {
  // Actions
  ADD_SEARCH_TERM,
  EDIT_DATE_RANGE,
  EDIT_SEARCH_TERM,
  REMOVE_SEARCH_TERM,
  TOGGLE_RECENT_SEARCHES,
  POPULATE_SEARCH_FORM,
  CLEAR_SEARCH_TERMS,
  SET_FFB_PARSER_ERROR,
  SET_QUERY_PARAMS,
  SET_ZQL_ARRAY,
  SET_ADDITIONAL_FILTERS,
  CLOSE_ADDITIONAL_FILTERS,
  SET_TRANSLATIONS,
  CLEAR_TRANSLATIONS,
  SET_SELECTED_LANGUAGES,
  REMOVE_LANGUAGE,
  TOGGLE_KEYWORD,
  SET_KEYWORD_COUNT,
  MAKE_TRANSLATIONS_OBSOLETE,
  // Action Creators
  addSearchTerm,
  editDateRange,
  editSearchTerm,
  removeSearchTerm,
  toggleRecentSearches,
  populateSearchForm,
  clearSearchTerms,
  setFFBParseErrors,
  setQueryParams,
  setZqlArray,
  setAdditionalFilters,
  closeAdditonalFilters,
  setTranslations,
  clearTranslations,
  selectLanguages,
  removeLanguage,
  onToggleKeyword,
  setEnabledKeywordsCount,
  makeTranslationsObsolete
};
